import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { WebmFile } from '@adornis/recorder/db/WebmFile.js';
import { Names } from '@ils/static/names.js';
import { transcribeFileAndSaveToAnswer } from '@ils/transcription/server/transcription.js';
import { exec } from 'child_process';

@Entity()
export class ILSFile extends AdornisFile {
  static override _class = Names.ILSFile._class;

  /** duration in seconds */
  @Field(type => Float)
  durationOfAudio?: number;

  static override async afterUpload({
    file,
    tmpPath,
    additionalInfo,
  }: {
    file: ILSFile;
    tmpPath: string;
    additionalInfo?: Record<string, string> | undefined;
  }) {
    await super.afterUpload({ file, tmpPath, additionalInfo });
    let mimeType = file.getMimeType();
    if (mimeType === 'video/webm') mimeType = 'audio/webm';

    if (!mimeType?.startsWith('audio')) return;
    try {
      if (additionalInfo?.transcribeAfterUpload === 'true') {
        if (!additionalInfo.answerID)
          throw new Error(
            'ILSFiles referencing audio files always need answerID in additionalInfo when transcribing. We need to know where to write the transcript.',
          );
        const answerID = additionalInfo.answerID;
        if (tmpPath) {
          await transcribeFileAndSaveToAnswer(tmpPath, mimeType, answerID);
        }
      }
    } catch (err) {
      logger.error({ err }, 'Error during transcription of webm file');
    }
  }
}

@Entity()
export class ILSWebmFile extends WebmFile {
  static override _class = Names.ILSWebmFile._class;

  @Field(type => Float) durationOfAudio: Maybe<number>;

  static override async afterUpload({
    file,
    tmpPath,
    additionalInfo,
  }: {
    file: ILSWebmFile;
    tmpPath: string;
    additionalInfo?: Record<string, string> | undefined;
  }) {
    await super.afterUpload({ file, tmpPath, additionalInfo });

    if (!additionalInfo?.answerForSlot) throw new Error('ILSWebmFile always needs answerForSlot in additionalInfo');
    if (!additionalInfo.answerType) throw new Error('ILSWebmFile always needs answerType in additionalInfo');

    // calculate audio length
    await new Promise<void>(resolve =>
      exec(`ffprobe -i ${tmpPath} -show_entries format=duration -v quiet -of csv="p=0"`, (err, stdout, stderr) => {
        // dont need to save to db, because files module will create file after afterUpload was run
        file.durationOfAudio = +stdout.trim();
        resolve();
      }),
    );

    // update answer
    const answers = await getCollection(Names.Answer._class);
    await answers.updateOne(
      { slotID: additionalInfo.answerForSlot },
      { $set: { [additionalInfo.answerType === 'combined' ? 'fileId' : 'onlyTraineeFileId']: file._id } },
    );

    //find answerID from fileID
    const answer = await answers.findOne({ fileId: file._id });
    if (answer) {
      try {
        await transcribeFileAndSaveToAnswer(tmpPath, file.getMimeType() ?? 'audio/webm', answer._id); //transcribe only if there exists an answer we can save it on
      } catch (err) {
        logger.error({ err }, 'Error during transcription of webm file');
      }
    }
  }
}
